<template>
    <div class="vuetify-wrapper v-application">
        <v-row no-gutters class="vuetify-wrapper v-application">
            <v-col cols="auto">
                <v-breadcrumbs :items="breadcrumbItems" class="pl-0">
                    <template v-slot:divider>
                        <v-icon>mdi-chevron-right</v-icon>
                    </template>
                </v-breadcrumbs>
            </v-col>
            <v-col cols="auto" class="d-flex align-center ml-auto">
                <v-btn
                    v-if="pharmacy.sepa_auth"
                    :href="`/pharmacies/${pharmacy._id}/sepa_download`"
                    target="_blank"
                    class="mr-2"
                    text
                    outlined
                    tile
                    small
                >
                    <v-icon class="mr-1" small>mdi-download</v-icon> SEPA-Mandat
                </v-btn>

                <v-menu offset-y>
                    <template v-slot:activator="{ on, attrs }">
                        <v-btn
                            v-bind="attrs"
                            v-on="on"
                            class="select-password-mail"
                            small
                        >
                            Passwort-Mail
                            <v-icon class="ml-1" small>mdi-menu-down</v-icon>
                        </v-btn>
                    </template>
                    <v-list>
                        <v-list-item @click.stop="openMailDialog('mail_pw_existing_customer')" link>
                            <v-list-item-title>
                                Passwort Bestandskunde
                            </v-list-item-title>
                        </v-list-item>
                        <v-list-item @click.stop="openMailDialog('mail_pw_reset')" link>
                            <v-list-item-title>
                                Passwort Reset
                            </v-list-item-title>
                        </v-list-item>
                        <v-list-item @click.stop="openMailDialog('mail_pw_new_customer')" link>
                            <v-list-item-title>
                                Passwort Neu-Kunde
                            </v-list-item-title>
                        </v-list-item>
                    </v-list>
                    <password-mail-dialog
                        v-model="isPasswordMailDialogOpen"
                        :mailTemplate="mailTemplate"
                        :pharmacyEmailAddress="pharmacy.email"
                        :pharmacyId="pharmacy._id"
                    />
                </v-menu>
                <v-btn
                    :href="`/pharmacies/${pharmacy._id}/edit`"
                    class="cyan lighten-3 ml-2 edit-pharmacy"
                    dark
                    small
                >
                    <v-icon small>mdi-pencil</v-icon>
                </v-btn>
            </v-col>
        </v-row>
        <v-row no-gutters class="v-application v-application--is-ltr">
            <v-col cols="auto" class="d-flex">
                <h2>{{ pharmacy.name_or_owner }}</h2>
                <v-chip-group class="ml-2">
                    <v-chip
                        v-if="pharmacy.sepa_auth"
                        color="error darken-2"
                        class="sepa-mandate-label"
                        x-small
                        label
                    >
                        SEPA Mandat
                    </v-chip>
                    <v-chip x-small label>
                        <span v-if="pharmacy.small_city">Kleinstadt</span>
                        <span v-else-if="pharmacy.small_city === false">Großstadt</span>
                        <span v-else>Unbekannte Stadtgröße</span>
                    </v-chip>
                    <v-chip
                        v-if="pharmacy.no_approval_required"
                        color="success"
                        class="no-approval-required-label"
                        x-small
                        label
                    >
                        direkte Zusage
                    </v-chip>
                    <v-chip
                        v-if="pharmacy.acquisition"
                        color="info"
                        x-small
                        label
                    >
                        {{ pharmacy.acquisition }}
                    </v-chip>
                    <v-chip
                        v-if="pharmacy.steffi_summary && pharmacy.steffi_summary.has_steffi_current_or_history"
                        :color="pharmacy.steffi_summary.color"
                        class="steffi-profile-label"
                        x-small
                        label
                        text-color="white"
                    >
                        <v-icon x-small class="mr-1">mdi-robot-outline</v-icon>
                        {{ pharmacy.steffi_summary.label }}
                    </v-chip>
                </v-chip-group>
            </v-col>
            <v-col
                v-if="tabNumber === 1"
                cols="auto"
                class="d-flex ml-auto"
            >
                <appointment-creator
                    :pharmacy="pharmacy"
                    @fetch-appointments="$refs.appointments.fetchAppointmentsCounts()"
                />
                <v-btn
                    :href="`/pharmacies/${pharmacy._id}/add_appointments`"
                    class="add-appointments ml-3"
                    small
                >
                    <v-icon small>mdi-plus</v-icon> mehrere Termine anlegen
                </v-btn>
            </v-col>
            <v-col cols="12" class="mt-1">
                <span v-if="pharmacy.user_id" class="mr-3">
                    <b>Benutzer:</b>
                    <a
                        :href="`/pharmacies/users/${pharmacy.user_id}`"
                        target="_blank"
                        class="username"
                    >
                        {{ pharmacy.user.firstname }} {{ pharmacy.user.lastname }}
                    </a>
                    <v-chip
                        v-if="pharmacy.user.terms_confirmed"
                        x-small
                        outlined
                        class="px-1"
                    >
                        <v-icon class="mr-1" x-small color="success">mdi-check</v-icon>
                        AGBs
                    </v-chip>
                    <v-chip
                        v-if="pharmacy.user.dataprotection_confirmed"
                        x-small
                        outlined
                        class="px-1"
                    >
                        <v-icon class="mr-1" x-small color="success">mdi-check</v-icon>
                        DSGVO
                    </v-chip>
                </span>
                <span v-if="pharmacy.customer_id" class="mr-3">
                    <b>Kundennummer:</b> {{ pharmacy.customer_id }}
                </span>
                <span v-else>
                    <v-btn
                        @click="isConfirmationDialogOpen = true"
                        class="mr-3 set-customer-id"
                        small
                    >
                        Kundennummer vergeben
                    </v-btn>
                    <confirmation-dialog
                        v-model="isConfirmationDialogOpen"
                        @save="generateCustomerId"
                        message="Bist Du sicher?"
                    />
                </span>
                <span class="mr-3">
                    <b>BtM-Nummer:</b>
                    <span v-if="pharmacy.btm">{{ pharmacy.btm }}</span>
                    <span v-else>Nein</span>
                </span>
                <span class="mr-3">
                    <b>Erstellt:</b>
                    <a
                        v-if="pharmacy.created_by"
                        :href="`/pharmacies/users/${pharmacy.created_by}`"
                        target="_blank"
                    >
                        {{ $formatDate(pharmacy.created) }}
                    </a>
                    <span v-else>{{ $formatDate(pharmacy.created) }}</span>
                </span>
                <span class="mr-3">
                    <b>Bearbeitet:</b>
                    <a
                        v-if="pharmacy.updated_by"
                        :href="`/pharmacies/users/${pharmacy.updated_by}`"
                        target="_blank"
                    >
                        {{ $formatDate(pharmacy.updated) }}
                    </a>
                    <span v-else>{{ $formatDate(pharmacy.updated) }}</span>
                </span>
            </v-col>
        </v-row>

        <v-row
            v-if="pharmacy.branches && pharmacy.branches.length > 1"
            no-gutters
            class="v-application v-application--is-ltr"
            >
            <v-col>
                <strong>Filialen:</strong>
                <span v-for="(branch, index) in pharmacy.branches" :key="branch._id">
                    <strong v-if="branch._id === pharmacy._id">
                        {{ branch.shortname }} ({{ branch.customer_id }})
                    </strong>
                    <a
                        v-else
                        :href="`/pharmacies/${branch._id}`"
                        target="_blank"
                        rel="noopener noreferrer"
                        class="text-decoration-none"
                    >
                        {{ branch.shortname }} ({{ branch.customer_id }})
                    </a>
                    <span v-if="index < pharmacy.branches.length - 1">, </span>
                </span>
            </v-col>
        </v-row>


        <v-row class="v-application">
            <v-col class="mb-4">
                <pharmacy-info
                    :pharmacy="pharmacy"
                    :knownPharmacists="pharmacists"
                    @get-pharmacy="getPharmacy"
                />
            </v-col>
        </v-row>
        <v-tabs
            v-model="tabNumber"
            center-active
            active-class=""
            show-arrows
        >
            <v-tab
                v-for="tab in tabs"
                :id="`${tab.value}-tab`"
                :key="tab.value"
            >
                {{ tab.text }}
            </v-tab>

        </v-tabs>
        <v-divider />
        <v-tabs-items v-model="tabNumber" class="tab-items-container">
            <v-tab-item>
                <pharmacists-info
                    :pharmacists="pharmacists"
                    :pharmacy="pharmacy"
                    @get-pharmacists="getPharmacists(); getPharmacy()"
                />
            </v-tab-item>
            <v-tab-item>
                <appointments-overview :pharmacy_id="pharmacyId" ref="appointments" />
            </v-tab-item>
            <v-tab-item>
                <ticket-overview :pharmacy_id="pharmacyId" />
            </v-tab-item>
            <v-tab-item>
                <linked-discounts
                    :pharmacyId="pharmacy._id"
                    :appliedDiscounts="pharmacy.applied_discounts"
                />
            </v-tab-item>
            <v-tab-item>
                <pharmacy-call-history-table
                    ref="call-history"
                    :pharmacyId="pharmacy._id"
                    :phone="pharmacy.phone"
                    :mobile="pharmacy.mobile"
                />
            </v-tab-item>
            <v-tab-item>
                <steffi-profile-tab :pharmacyId="pharmacyId" />
            </v-tab-item>
            <v-tab-item>
                <custom-invoices recipient-type="pharmacy" :recipient-id="pharmacyId" />
            </v-tab-item>
        </v-tabs-items>
    </div>
</template>

<script>
import axios from 'axios';
import { mapMutations } from 'vuex';

import AppointmentsOverview from '../components/appointments/AppointmentsOverview.vue';
import TicketOverview from '../components/tickets/TicketOverview.vue';
import LinkedDiscounts from '../components/pharmacy/discounts/LinkedDiscounts.vue';
import PharmacistsInfo from '../components/pharmacy/detail-page/PharmacistsInfoTable.vue';
import PharmacyInfo from '../components/pharmacy/detail-page/PharmacyInfo.vue';
import AppointmentCreator from "../components/appointments/AppointmentCreator.vue";
import PasswordMailDialog from '../components/pharmacy/detail-page/PasswordMailDialog.vue';
import ConfirmationDialog from '../components/appointments/ConfirmationDialog.vue';
import PharmacyCallHistoryTable from '../components/sales-calls/PharmacyCallHistoryTable.vue';
import SteffiProfileTab from '../components/pharmacy/detail-page/SteffiProfileTab.vue';

export default {
    components: {
        PharmacyInfo,
        PharmacistsInfo,
        AppointmentsOverview,
        TicketOverview,
        LinkedDiscounts,
        PharmacyCallHistoryTable,
        AppointmentCreator,
        PasswordMailDialog,
        ConfirmationDialog,
        SteffiProfileTab,
    },
    data() {
        return {
            pharmacy: {},
            pharmacists: [],
            pharmacistsServiceCategories: {},
            isPasswordMailDialogOpen: false,
            isConfirmationDialogOpen: false,
            mailTemplate: 'mail_pw_existing_customer',
            tabNumber: 1,
            tabs: [
                {
                    text: 'Freiberufler',
                    value: 'freelancers',
                },
                {
                    text: 'Termine',
                    value: 'appointments',
                },
                {
                    text: 'Tickets',
                    value: 'tickets',
                },
                {
                    text: 'Rabatte',
                    value: 'discounts',
                },
                {
                    text: 'Calls',
                    value: 'calls',
                },
                {
                    text: 'Steffi',
                    value: 'steffi',
                },
                {
                    text: 'Individuelle Rechnungen',
                    value: 'custom-invoices',
                },
            ],
        }
    },
    computed: {
        breadcrumbItems() {
            return [
                {
                    text: 'Apotheken',
                    href: '/pharmacies',
                },
                {
                    text: this.pharmacy?.name,
                    href: `/pharmacies/${this.pharmacy?._id}`,
                },
                {
                    text: this.tabs[this.tabNumber],
                    href: `/pharmacies/${this.pharmacy?._id}`,
                },
            ]
        },
        pharmacyId() {
            return window.location.pathname.split('/pharmacies/')[1];
        }
    },
    methods: {
        async getPharmacy() {
            try {
                const response = await axios(`/pharmacies/${this.pharmacyId}/pharmacy`);
                this.pharmacy = response.data;
            } catch {
                this.alertError()
            }
        },
        async getPharmacists() {
            try {
                const response = await axios(`/pharmacies/${this.pharmacyId}/pharmacists`);
                this.pharmacists = response.data.users;
                this.pharmacistsServiceCategories = response.data.service_categories;
            } catch {
                this.alertError()
            }
        },
        async generateCustomerId() {
            try {
                const response = await axios.post(`/pharmacies/${this.pharmacyId}/set_customer_id`);
                this.alertSuccess(response.data.message);
                this.getPharmacy();
            } catch {
                this.alertError()
            }
        },
        // async saveCall() {
        //     await this.$refs['call-history'].addCallHistory();
        //     this.isConfirmationDialogOpen = false
        // },

        openMailDialog(type) {
            this.mailTemplate = type;
            this.isPasswordMailDialogOpen = true;
        },
        ...mapMutations({
            alertError: 'alert/error',
            alertSuccess: 'alert/success',
        })
    },
    async created() {
        await this.getPharmacy();
        await this.getPharmacists();
    },
}
</script>
<style>
.tab-items-container {
    overflow: initial !important;
}
</style>
<style scoped></style>
